import { Status } from '@subaio/api-types/budget'
import { type Consent, ConsentType } from '@subaio/api-types/shared'
import { type BoundHandlers, type StepHandlerResult, stepMetaHistoryClear } from '@subaio/components'
import { type Api, delay } from '@subaio/utils'

import { BrandSteps } from 'apiTypes/BrandSteps'
import type { StatusUserDetails } from 'apiTypes/StatusUserDetails'

import { type Step, StepType } from './Step'

export enum ApplicantType {
	SingleApplicant = 'SingleApplicant',
	MultipleApplicants = 'MultipleApplicants',
}

type Options = {
	userId: string
	budgetId: string
	sessionId: string
	brandSteps: Array<BrandSteps>
	redirectUrl: string | null

	api: Api

	onLogEvent: (log: string) => void
}

export const defaultBrandSteps: Array<BrandSteps> = [
	BrandSteps.Introduction,
	BrandSteps.Consent,
	BrandSteps.Psd2Login,
	BrandSteps.BudgetEditForm,
]

export const getStepHandlers = ({ userId, budgetId, sessionId, brandSteps, redirectUrl, api, onLogEvent }: Options) => {
	/**
	 * A list of steps that are allowed to be shown in this flow.
	 * There are some exceptions since not all steps are configurable:
	 * * No `BudgetEditForm`, we show `BudgetPoll` instead to handle any budget creation related errors and to ready it.
	 * * A `redirectUrl` is returned on login, we replace `Success` with `Redirect`.
	 */
	const stepsToShow = brandSteps && brandSteps.length > 0 ? brandSteps : defaultBrandSteps
	const getStatus = () =>
		api.get<{ status: Status; userDetails: Array<StatusUserDetails>; isBankDataReceived: boolean }>(
			`/budgets/${budgetId}/status`,
		)
	const getOtherApplicantStep = (userDetails: Array<StatusUserDetails>): Step | undefined => {
		const ownDetails = userDetails.find((details) => details.userId === userId)
		const isBankDataPending =
			!ownDetails?.bankDataReceivedAt || userDetails.some((details) => !details.bankDataReceivedAt)
		// Prefer the primary applicant when arrival times match so only one applicant can edit.
		if (
			isBankDataPending ||
			!userDetails.every(
				(details) =>
					details.userId === userId ||
					(details.bankDataReceivedAt &&
						(details.bankDataReceivedAt < ownDetails.bankDataReceivedAt! ||
							(details.bankDataReceivedAt === ownDetails.bankDataReceivedAt && ownDetails.type === 'PRIMARY'))),
			)
		) {
			return { type: StepType.MissingOtherApplicant, isBankDataPending }
		}
	}
	const getStepAfterBankData = (userDetails: Array<StatusUserDetails>): Step => {
		const otherApplicantStep = getOtherApplicantStep(userDetails)
		if (otherApplicantStep) return otherApplicantStep
		if (stepsToShow.includes(BrandSteps.Assets)) return { type: StepType.Assets }
		return {
			type: stepsToShow.includes(BrandSteps.BudgetEditForm) ? StepType.BudgetEditForm : StepType.BudgetPoll,
		}
	}

	return {
		onInitial: async () => {
			const [{ status, userDetails, isBankDataReceived }, consents] = await Promise.all([
				getStatus(),
				api.get<Array<Consent>>('/consents'),
			])

			if (!consents.some(({ type }) => type === ConsentType.Budget) && stepsToShow.includes(BrandSteps.Introduction)) {
				return { type: StepType.Introduction, brandSteps } satisfies Step
			}

			if (
				(status === Status.Uninitialized || status === Status.MissingCoapplicantData) &&
				stepsToShow.includes(BrandSteps.Psd2Login)
			) {
				return {
					type: StepType.Accounts,
					isBankDataReceived,
				} satisfies Step
			}

			return getStepAfterBankData(userDetails)
		},
		onIntroductionNext: async () => {
			if (stepsToShow.includes(BrandSteps.Consent)) {
				return {
					type: StepType.Terms,
				} satisfies Step
			}

			if (stepsToShow.includes(BrandSteps.Psd2Login)) {
				return {
					type: StepType.Accounts,
					isBankDataReceived: false,
				} satisfies Step
			}

			return getStepAfterBankData((await getStatus()).userDetails)
		},
		onTermsAccept: async (text: string) => {
			onLogEvent('TERMS_ACCEPTED')
			await api.post<Array<Consent>>('/consents', { text, type: ConsentType.Budget })

			if (stepsToShow.includes(BrandSteps.Psd2Login)) {
				return {
					type: StepType.Accounts,
					isBankDataReceived: false,
				} satisfies Step
			}

			return getStepAfterBankData((await getStatus()).userDetails)
		},
		onAccountSelectComplete: async () => {
			// Poll user details for when bank data is received for this user
			let userDetails: Array<StatusUserDetails>
			do {
				userDetails = (await getStatus()).userDetails
			} while (
				!userDetails.some((details) => details.userId === userId && details.bankDataReceivedAt) &&
				(await delay(2).then(() => true))
			)

			return getStepAfterBankData(userDetails)
		},
		onOtherApplicantStatus: async () => getStepAfterBankData((await getStatus()).userDetails),
		onAssetsFinish: async () => {
			const otherApplicantStep = getOtherApplicantStep((await getStatus()).userDetails)
			if (otherApplicantStep) return otherApplicantStep

			if (stepsToShow.includes(BrandSteps.BudgetEditForm)) {
				return {
					type: StepType.BudgetEditForm,
				} satisfies Step
			}

			return {
				type: StepType.BudgetPoll,
			} satisfies Step
		},
		onBudgetEditFinish: async (): Promise<StepHandlerResult<Step>> => {
			await api.post(`/budgets/${budgetId}/completions`)
			await api.delete(`/users/${sessionId}`)
			onLogEvent('BUDGET_COMPLETE')

			if (redirectUrl) {
				return stepMetaHistoryClear({
					type: StepType.Redirect,
					redirectUrl,
				} satisfies Step)
			}

			return stepMetaHistoryClear({
				type: StepType.Success,
			} satisfies Step)
		},
	}
}

export type StepHandlers = BoundHandlers<Step, ReturnType<typeof getStepHandlers>>
